import { useState, type FormEvent } from "react";
import { filterCategories } from "@/lib/site";

const FORM_ID = "newsletter-subscribe";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dahfm6jp14a1h3mr6kug";

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function NewsletterForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，新的进展汇总会发送到你的邮箱。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") {
        return;
      }
      const text = String(value).trim();
      if (text) {
        payload.append(key, text);
      }
    });

    setStatus("submitting");
    setMessage("正在提交…");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，新的进展汇总会发送到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          isSpam ? "提交未能通过校验，请稍后重试。" : serverMsg || "提交失败，请稍后再试。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络连接异常，请稍后重试。");
    }
  };

  const statusClass =
    status === "error" ? "text-primary-200" : "text-background-100";

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      action={SUBMIT_ADDR}
      method="post"
      onSubmit={handleSubmit}
      className="w-full"
    >
      <label
        htmlFor={`${FORM_ID}-email`}
        className="block font-heading text-base font-semibold text-background-50"
      >
        订阅更新提醒
      </label>
      <p className="mt-2 text-xs leading-relaxed text-background-100/70">
        按日汇总你关注领域的进展节点，每天一封，随时可退订。
      </p>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          id={`${FORM_ID}-email`}
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          className="h-11 flex-1 rounded-md border border-background-50/20 bg-background-50/10 px-3 text-sm text-background-50 placeholder:text-background-100/50 focus:border-primary-400 focus:outline-none"
        />
        <select
          name="interest"
          defaultValue="all"
          aria-label="关注领域"
          className="h-11 rounded-md border border-background-50/20 bg-background-50/10 px-3 text-sm text-background-50 focus:border-primary-400 focus:outline-none sm:w-36"
        >
          <option value="all" className="text-foreground-950">
            全部领域
          </option>
          {filterCategories.map((item) => (
            <option key={item.id} value={item.id} className="text-foreground-950">
              {item.label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex h-11 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? "提交中…" : "立即订阅"}
        </button>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-aux-field"
      />

      {status !== "idle" ? (
        <p className={`mt-3 text-xs ${statusClass}`} role="status" aria-live="polite">
          {message}
        </p>
      ) : null}
    </form>
  );
}